import { RadioProps, useRadio, VisuallyHidden } from '@heroui/react'
import { useTranslation } from 'react-i18next'
import type { ReturnsProductDetailsResponseVariantInfo } from '@itsrever/rever-api'
import { useCredit } from '@/hooks'
import { useAppSelector } from '@/redux/hooks'
import { hasVariantStock } from '../helpers/has-variant-stock'
import { calculateAmountDue } from '../helpers/calculate-amount-due'

interface ProductVariantButtonProps extends RadioProps {
    variant: ReturnsProductDetailsResponseVariantInfo
}

export const ProductVariantButton = ({
    variant,
    ...props
}: ProductVariantButtonProps) => {
    const {
        Component,
        getBaseProps,
        getWrapperProps,
        getInputProps,
        getLabelProps,
        getLabelWrapperProps,
        getControlProps
    } = useRadio(props)

    const {
        t,
        i18n: { language }
    } = useTranslation()

    const settings = useAppSelector(
        (store) => store.settingsApi.settings.response
    )

    const isHoff = settings.ecommerce_group === 'hoff'
    const isMisako = settings.ecommerce_group === 'misako'

    const { remainingCredit } = useCredit()

    const hasStock = hasVariantStock(variant)

    const parsedVariantPrice = variant.price?.amount
        ? parseFloat(variant.price?.amount)
        : null

    const amountDue =
        parsedVariantPrice !== null
            ? calculateAmountDue(parsedVariantPrice, remainingCredit)
            : null

    if (isHoff) {
        return (
            <Component
                {...getBaseProps()}
                data-stock={hasStock}
                className="flex min-h-[58px] cursor-pointer items-center p-2.5 transition-all hover:bg-neutral-100 data-[focus=true]:border-neutral-600 data-[selected=true]:bg-neutral-100"
            >
                <VisuallyHidden>
                    <input {...getInputProps()} />
                </VisuallyHidden>
                <VisuallyHidden>
                    <span {...getWrapperProps()}>
                        <span {...getControlProps()} />
                    </span>
                </VisuallyHidden>
                <div
                    {...getLabelWrapperProps()}
                    className="flex h-full flex-1 items-center justify-between gap-3"
                >
                    <span
                        {...getLabelProps()}
                        className="!line-clamp-2 inline-block text-sm font-medium md:text-base"
                    >
                        {variant.name}
                    </span>
                    {!hasStock ? (
                        <span className="inline-block text-xs font-medium">
                            {t('full_catalog.no_stock')}
                        </span>
                    ) : remainingCredit > 0 ? (
                        <div className="flex shrink-0 flex-col items-end gap-0.5">
                            <p className="font-haas text-sm font-medium text-black">
                                {amountDue !== null
                                    ? amountDue?.toLocaleString(language, {
                                          style: 'currency',
                                          currency:
                                              variant.price?.currency_code,
                                          minimumFractionDigits:
                                              amountDue === 0 ? 0 : 2,
                                          maximumFractionDigits: 2
                                      })
                                    : null}
                            </p>
                            <p className="text-right font-haas text-xs font-light text-neutral-500">
                                {parsedVariantPrice !== null
                                    ? parsedVariantPrice?.toLocaleString(
                                          language,
                                          {
                                              style: 'currency',
                                              currency:
                                                  variant.price?.currency_code,
                                              minimumFractionDigits: 2,
                                              maximumFractionDigits: 2
                                          }
                                      )
                                    : null}{' '}
                                {t('full_catalog.original_price')}
                            </p>
                        </div>
                    ) : parsedVariantPrice !== null ? (
                        <p>
                            {parsedVariantPrice?.toLocaleString(language, {
                                style: 'currency',
                                currency: variant.price?.currency_code,
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            })}
                        </p>
                    ) : null}
                </div>
            </Component>
        )
    }

    if (isMisako) {
        return (
            <Component
                {...getBaseProps()}
                data-stock={hasStock}
                className="flex min-h-[58px] cursor-pointer items-center border border-solid border-neutral-300 px-3 py-2 transition-all hover:bg-neutral-100 data-[focus=true]:bg-neutral-100 data-[selected=true]:bg-neutral-100"
            >
                <VisuallyHidden>
                    <input {...getInputProps()} />
                </VisuallyHidden>
                <VisuallyHidden>
                    <span {...getWrapperProps()}>
                        <span {...getControlProps()} />
                    </span>
                </VisuallyHidden>
                <div
                    {...getLabelWrapperProps()}
                    className="flex h-full flex-1 items-center justify-between gap-3"
                >
                    <span
                        {...getLabelProps()}
                        className="!line-clamp-2 inline-block font-inter text-sm font-light uppercase tracking-wider"
                    >
                        {variant.name}
                    </span>
                    {!hasStock ? (
                        <span className="inline-block py-3.5 text-xs font-medium">
                            {t('full_catalog.no_stock')}
                        </span>
                    ) : remainingCredit > 0 ? (
                        <div className="flex shrink-0 flex-col items-end gap-1">
                            <p className="font-inter text-sm">
                                {amountDue !== null
                                    ? amountDue?.toLocaleString(language, {
                                          style: 'currency',
                                          currency:
                                              variant.price?.currency_code,
                                          minimumFractionDigits:
                                              amountDue === 0 ? 0 : 2,
                                          maximumFractionDigits: 2
                                      })
                                    : null}
                            </p>
                            <p className="text-right font-inter text-xs font-light uppercase tracking-wider text-neutral-400">
                                {parsedVariantPrice !== null
                                    ? parsedVariantPrice?.toLocaleString(
                                          language,
                                          {
                                              style: 'currency',
                                              currency:
                                                  variant.price?.currency_code,
                                              minimumFractionDigits: 2,
                                              maximumFractionDigits: 2
                                          }
                                      )
                                    : null}{' '}
                                {t('full_catalog.original_price')}
                            </p>
                        </div>
                    ) : parsedVariantPrice !== null ? (
                        <p>
                            {parsedVariantPrice?.toLocaleString(language, {
                                style: 'currency',
                                currency: variant.price?.currency_code,
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            })}
                        </p>
                    ) : null}
                </div>
            </Component>
        )
    }

    return (
        <Component
            {...getBaseProps()}
            data-stock={hasStock}
            className="flex min-h-[60px] cursor-pointer items-center rounded-md border-1 border-solid border-neutral-300 px-4 pb-1.5 pt-2 transition-all hover:border-neutral-800 hover:bg-neutral-100 data-[focus=true]:border-neutral-600 data-[selected=true]:border-neutral-600 data-[stock=false]:bg-neutral-200 hover:[&:not(:data-[stock=false])]:data-[selected=true]:bg-white"
        >
            <VisuallyHidden>
                <input {...getInputProps()} />
            </VisuallyHidden>
            <VisuallyHidden>
                <span {...getWrapperProps()}>
                    <span {...getControlProps()} />
                </span>
            </VisuallyHidden>
            <div
                {...getLabelWrapperProps()}
                className="flex h-full flex-1 items-center justify-between gap-3"
            >
                <span
                    {...getLabelProps()}
                    className="!line-clamp-2 inline-block text-sm font-medium md:text-base"
                >
                    {variant.name}
                </span>
                {!hasStock ? (
                    <span className="inline-block py-3.5 text-xs font-medium">
                        {t('full_catalog.no_stock')}
                    </span>
                ) : remainingCredit > 0 ? (
                    <div className="flex shrink-0 flex-col items-end gap-1">
                        <p
                            style={{
                                boxShadow: '0px 0px 5.7px 0px #FFF inset'
                            }}
                            className="flex w-fit shrink-0 items-center justify-center rounded-full bg-radial-gradient from-brand-800 to-brand-800 px-3 py-0.5 text-sm text-white"
                        >
                            {amountDue !== null
                                ? amountDue?.toLocaleString(language, {
                                      style: 'currency',
                                      currency: variant.price?.currency_code,
                                      minimumFractionDigits:
                                          amountDue === 0 ? 0 : 2,
                                      maximumFractionDigits: 2
                                  })
                                : null}
                        </p>
                        <p className="text-right text-xs font-light text-neutral-400">
                            {parsedVariantPrice !== null
                                ? parsedVariantPrice?.toLocaleString(language, {
                                      style: 'currency',
                                      currency: variant.price?.currency_code,
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2
                                  })
                                : null}{' '}
                            {t('full_catalog.original_price')}
                        </p>
                    </div>
                ) : parsedVariantPrice !== null ? (
                    <p>
                        {parsedVariantPrice?.toLocaleString(language, {
                            style: 'currency',
                            currency: variant.price?.currency_code,
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                        })}
                    </p>
                ) : null}
            </div>
        </Component>
    )
}
